import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Col, Container, Row } from 'react-bootstrap';
import Blockquote from '../../../../components/Blockquote';
import Button from '../../../../components/Button';
import CircledIcon from '../../../../components/CircledIcon';
import DefaultCard from '../../../../components/DefaultCard';
import Link from '../../../../components/Link';
import { RadioGroup } from '../../../../components/Radio';
import RadioWithImage from '../../../../components/RadioWithImage';
import Support from '../../../../components/Support';
import SupportMobile from '../../../../components/Support/SupportMobile';
import useModal from '../../../../hooks/useModal';
import { stopVideo } from '../../../../utils/utils';
import VideoEmbedModal from '../../VideoEmbedModal/VideoEmbedModal';

const radioCards = [
  {
    imageRef: 'rollover-central.png',
    cardTitle: 'Rollover using RolloverCentral',
    id: 'rollover-central',
  },

  {
    imageRef: 'rollover-directly-to-me.png',
    cardTitle: 'Rollover Directly to Me',
    id: 'rollover-directly-to-me',
  },

  {
    imageRef: 'payment-in-cash.png',
    cardTitle: 'Payment in Cash Directly to Me',
    id: 'payment-in-cash-directly-to-me',
  },
];

const blockQuotes = [
  {
    id: 'rollover-central',
    blockquoteText: (
      <Container className={'px-0 d-flex flex-column'}>
        <span className={'text-fs-6 text-ligther-grey fw-normal'}>
          Rollover using Rollover Central: Did You Know?
        </span>
        <span className={'text-fs-6 text-lighter-grey'}>
          We partner with RolloverCentral to make it easy to roll over your
          money. If you choose this option then an electronic rollover will be
          made directly to your provider. Would you like to see your options for
          <span className={'d-inline-block ms-1'}>
            <Link className={'p-0'} isBlue>
              RolloverCentral?
            </Link>
          </span>
        </span>
      </Container>
    ),
  },

  {
    id: 'rollover-directly-to-me',
    blockquoteText: (
      <Container className={'px-0 d-flex flex-column'}>
        <span className={'text-fs-6 text-ligther-grey fw-normal'}>
          Rollover directly to me: Did You Know?
        </span>
        <span className={'text-fs-6 text-lighter-grey'}>
          If you choose this option then a check made out to your provider will
          be sent directly to you and you will be responsible for forwarding to
          your provider.
        </span>
      </Container>
    ),
  },

  {
    id: 'payment-in-cash-directly-to-me',
    blockquoteText: (
      <Container className={'px-0 d-flex flex-column'}>
        <span className={'text-fs-6 text-ligther-grey fw-normal'}>
          Payment in cash directly to me : Did You Know?
        </span>
        <span className={'text-fs-6 text-lighter-grey'}>
          I elect my lump sum benefit be paid directly to me. I understand that
          a 20% mandatory federal tax withholding will apply and that the amount
          withheld may not be sufficient to pay the applicable federal taxes
          that may be due on the distribution. I further understand that
          applicable state taxes may be due on the distribution.
        </span>
        <br />
        <span className={'text-fs-6 text-lighter-grey'}>
          I also further understand that if I have not attained age 59½, an
          additional 10% early payment tax may apply.
        </span>
      </Container>
    ),
  },
];

export type lumpSumDistributionProps = {
  data: any;
  collectStepData: (data: any) => void;
};

const LumpSumDistribution = ({
  data,
  collectStepData,
}: lumpSumDistributionProps) => {
  const [
    showVideoEmbedModal,
    handleShowVideoEmbedModal,
    handleCloseVideoEmbedModal,
  ] = useModal();

  const handleRolloverPayment = (e: string | number) => {
    collectStepData({
      ...data,
      rolloverPaymentOption: e,
    });
  };

  return (
    <Container
      className={'cnd-custom-container position-relative px-0 mb-11 mb-lg-13'}
    >
      <Row>
        <Col className={'mt-11 col-12 col-lg-9 mb-12 pb-10'}>
          <DefaultCard
            variant="blue-motif"
            leftHeading="Rollover Central"
            leftSubheading="We partner with RolloverCentral to make it easy to roll over your money."
            button={
              <Button
                onClick={handleShowVideoEmbedModal}
                variant="primary-shadow"
                size="sm"
              >
                Watch Now
              </Button>
            }
          />
          <VideoEmbedModal
            embeddedNode={
              <div className={'d-flex justify-content-center'}>
                <iframe
                  title={'rollover'}
                  src={
                    'https://app.rollovercentral.com/Video/Video?eqs=FMuPQAOqIkWGGBNrmnatuA%3D%3D'
                  }
                  width={640}
                  height={480}
                ></iframe>
              </div>
            }
            showVideoEmbedModal={showVideoEmbedModal}
            handleCloseVideoEmbedModal={handleCloseVideoEmbedModal}
            stopVideo={stopVideo}
            title={'Rollover Central'}
          />
          <Col className={'col-12'}>
            <div className="d-flex flex-row justify-content-between align-items-center">
              <h5 className={'mt-12 text-sm-20-lg-24'}>
                Lump Sum Distribution Type
              </h5>
              <div className={'mt-12 text-sm-20-lg-24 d-lg-none'}>
                <SupportMobile>
                  <div className="p-0 mt-4">
                    <span className="fw-bold text-caption">
                      Retirement Case Administrator
                    </span>
                    <div className="mt-4">
                      <CircledIcon
                        icon={light('user-headset')}
                        label="Jessica Wright"
                      />
                    </div>
                    <div className="mt-4">
                      <CircledIcon
                        icon={light('phone')}
                        label="1-800-888-8888 x 1234"
                      />
                    </div>
                  </div>
                </SupportMobile>
              </div>
            </div>
          </Col>
          <Col className={'col-12'}>
            <span className={'text-fs-6 text-ligther-grey fw-light'}>
              Choose how you would like your employee contribution balance of
              $204,360.25 paid to you:
            </span>
          </Col>
        </Col>
        <div className="d-none d-lg-flex justify-content-lg-end col-lg-3">
          <section className="mt-11">
            <Support>
              <div className="p-0 mt-4">
                <span className="fw-bold text-caption">
                  Retirement Case Administrator
                </span>
                <div className="mt-4">
                  <CircledIcon
                    icon={light('user-headset')}
                    label="Jessica Wright"
                  />
                </div>
                <div className="mt-4">
                  <CircledIcon
                    icon={light('phone')}
                    label="1-800-888-8888 x 1234"
                  />
                </div>
              </div>
            </Support>
          </section>
        </div>
      </Row>

      <RadioGroup
        name={'no-labels'}
        value={data.rolloverPaymentOption}
        onChange={handleRolloverPayment}
      >
        <Row>
          {radioCards.map((radioCard, idx) => {
            return (
              <Col key={idx} className={'col-12 col-lg-3 mb-11 pb-1'}>
                <RadioWithImage
                  cardData={radioCard}
                  selected={data.rolloverPaymentOption !== radioCard.id}
                />
              </Col>
            );
          })}
        </Row>
      </RadioGroup>

      {blockQuotes.map((blockQuote, idx) => {
        return (
          <Container
            key={idx}
            className={`px-0 ${
              data.rolloverPaymentOption === blockQuote.id
                ? 'd-block'
                : 'd-none'
            }`}
          >
            <Blockquote>{blockQuote.blockquoteText}</Blockquote>
          </Container>
        );
      })}
    </Container>
  );
};

export default LumpSumDistribution;
